import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tradingService, type AlgorithmStats } from '../../services/trading';

interface AlgorithmAnalyticsProps {
  accountFilter?: string;
  tickerFilter?: string;
  algorithmFilter?: string;
  timeRange?: 'today' | 'week' | 'month' | 'year' | 'all';
  showSummaryCards?: boolean; // Whether to show the summary cards (Total Trades, Win Rate, etc.)
}

export function AlgorithmAnalytics({ accountFilter, tickerFilter, algorithmFilter, timeRange = 'all', showSummaryCards = true }: AlgorithmAnalyticsProps = {}) {
  // Fetch aggregate stats from trade_aggregates table
  const { data: algorithmMetrics = [], isLoading } = useQuery({
    queryKey: ['aggregateStats', accountFilter, tickerFilter, algorithmFilter, timeRange],
    queryFn: async () => {
      try {
        return await tradingService.getAggregateStats(accountFilter, tickerFilter, algorithmFilter, timeRange);
      } catch (err) {
        console.error('Aggregate stats fetch error:', err);
        return [];
      }
    },
  });

  // Calculate overall metrics
  const overallMetrics = useMemo(() => {
    const total = algorithmMetrics.reduce(
      (acc, metrics) => ({
        totalTrades: acc.totalTrades + metrics.totalTrades,
        totalPnL: acc.totalPnL + metrics.totalPnL,
        wins: acc.wins + metrics.wins,
        losses: acc.losses + metrics.losses,
      }),
      { totalTrades: 0, totalPnL: 0, wins: 0, losses: 0 }
    );

    // Win rate is wins / (wins + losses) * 100
    const totalCompletedTrades = total.wins + total.losses;

    return {
      ...total,
      winRate: totalCompletedTrades > 0 ? (total.wins / totalCompletedTrades) * 100 : 0,
    };
  }, [algorithmMetrics]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overall Summary Cards - Only shown when showSummaryCards is true */}
      {showSummaryCards && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Trades</div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{overallMetrics.totalTrades}</div>
          </div>
          <div className="bg-white dark:bg-dark-card rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Win Rate</div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              {formatPercent(overallMetrics.winRate)}
            </div>
          </div>
          <div className="bg-white dark:bg-dark-card rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total P&L</div>
            <div className={`mt-2 text-3xl font-bold ${overallMetrics.totalPnL >= 0 ? 'text-rh-green' : 'text-rh-red'}`}>
              {formatCurrency(overallMetrics.totalPnL)}
            </div>
          </div>
          <div className="bg-white dark:bg-dark-card rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Win/Loss</div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              {overallMetrics.wins}/{overallMetrics.losses}
            </div>
          </div>
        </div>
      )}

      {/* Algorithm Comparison Table */}
      <div className="bg-white dark:bg-dark-card shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Algorithm Performance Comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Algorithm
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Trades
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Win Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total P&L
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Avg Profit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Avg Loss
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Best Trade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Worst Trade
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
              {algorithmMetrics.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No algorithm data available for selected time period
                  </td>
                </tr>
              ) : (
                algorithmMetrics.map((metrics) => (
                  <tr key={metrics.algorithmType} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        {metrics.algorithmType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {metrics.totalTrades}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={metrics.winRate >= 50 ? 'text-rh-green font-medium' : 'text-gray-900 dark:text-white'}>
                        {formatPercent(metrics.winRate)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={metrics.totalPnL >= 0 ? 'text-rh-green font-medium' : 'text-rh-red font-medium'}>
                        {formatCurrency(metrics.totalPnL)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-rh-green">
                      {formatCurrency(metrics.avgWin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-rh-red">
                      {formatCurrency(metrics.avgLoss)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-rh-green">
                      {formatCurrency(metrics.bestTrade)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-rh-red">
                      {formatCurrency(metrics.worstTrade)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}